// Loading states for the workspace (DESIGN.md "Shell", "Motion").
//   ShellSkeleton  the whole workspace frame while sign-in resolves on a cold
//                  load: aurora, the sidebar's brand and grouped rows, the top
//                  bar and a page-header skeleton, so the shell paints at once
//                  and fills in instead of a spinner on black.
//   RouteSkeleton  the content region while a lazily loaded page arrives; the
//                  shell around it stays put.
//   RouteFallback  the Suspense fallback for every route: RouteSkeleton inside
//                  the shell, a quiet canvas outside it (public pages).
// Skeleton bars are static: no shimmer per bar. One soft pulse on the group
// (.sg-skel-pulse) reads as "loading" and stops under reduced motion.

import React, { useContext } from "react";
import { InShellContext } from "./shellContext";
import Aurora from "./Aurora";
import { NexusGlyph } from "./icons";
import "../layout/workspace.css";

const Bar = ({ w, h = 10, className = "" }) => (
  <span
    className={`sg-skel-bar ${className}`.trim()}
    style={{ width: w, height: h }}
  />
);

/** The page header and a first panel: what every workspace page opens with. */
export const RouteSkeleton = ({ label = "Loading page" }) => (
  <div
    className="sg-route-skel sg-skel-pulse"
    role={label ? "status" : undefined}
    aria-live={label ? "polite" : undefined}
  >
    {label && <span className="sr-only">{label}</span>}
    <div className="sg-route-skel-head" aria-hidden="true">
      <div className="flex flex-col gap-3">
        <Bar w={220} h={22} />
        <Bar w={140} h={10} />
      </div>
      <Bar w={128} h={32} className="sg-skel-pill" />
    </div>
    <div className="sg-route-skel-panel" aria-hidden="true">
      {[0.92, 0.78, 0.86, 0.7, 0.82].map((width, index) => (
        <div key={index} className="sg-route-skel-row">
          <Bar w={28} h={28} className="sg-skel-round" />
          <Bar w={`${Math.round(width * 38)}%`} h={10} />
          <Bar w="14%" h={10} className="ml-auto" />
        </div>
      ))}
    </div>
  </div>
);

const SIDEBAR_GROUPS = [4, 3, 4];

/** The full workspace frame, before the user (and so their role) is known. */
export const ShellSkeleton = () => (
  <div
    className="nx-app nx-workspace sg-shell sg-shell-skel"
    role="status"
    aria-live="polite"
  >
    <span className="sr-only">Loading your workspace</span>
    <Aurora />
    <aside className="sg-sidebar" aria-hidden="true">
      <div className="sg-side-brand">
        <span className="sg-side-brandlink">
          <span className="sg-side-mark">
            <NexusGlyph size={14} />
          </span>
          <span className="sg-side-label sg-side-brandtext">
            <span className="sg-side-wordmark">NexusDialer</span>
          </span>
        </span>
      </div>
      <div className="sg-side-nav sg-skel-pulse">
        {SIDEBAR_GROUPS.map((count, group) => (
          <div key={group} className="sg-side-group">
            <div className="sg-side-grouplabel">
              <Bar w={44} h={7} />
            </div>
            {Array.from({ length: count }, (_, index) => (
              <div key={index} className="sg-side-item">
                <Bar w={16} h={16} className="sg-skel-round" />
                <Bar
                  w={`${48 + ((index * 17 + group * 11) % 30)}%`}
                  h={9}
                  className="sg-side-label"
                />
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="sg-side-foot">
        <div className="sg-account-trigger">
          <Bar w={28} h={28} className="sg-skel-round" />
          <span className="sg-account-text sg-side-label">
            <Bar w="70%" h={9} />
            <Bar w="40%" h={7} className="mt-1.5" />
          </span>
        </div>
      </div>
    </aside>
    <div className="sg-main" aria-hidden="true">
      <header className="sg-topbar">
        <div className="sg-top-left" />
        <div className="sg-top-center">
          <span className="sg-palette-trigger sg-skel-trigger" />
        </div>
        <div className="sg-top-right">
          <Bar w={96} h={28} className="sg-skel-pill" />
        </div>
      </header>
      <main className="sg-content">
        <div className="sg-content-inner">
          <RouteSkeleton label="" />
        </div>
      </main>
    </div>
  </div>
);

/** Suspense fallback: the content skeleton inside the shell, else a calm canvas. */
export const RouteFallback = () => {
  const inShell = useContext(InShellContext);
  if (inShell) return <RouteSkeleton />;
  return (
    <div className="sg-boot" role="status" aria-live="polite">
      <span className="sr-only">Loading</span>
      <Aurora />
    </div>
  );
};

export default ShellSkeleton;
